/**
 * Mailing list API service.
 * - Public subscribe: POST without auth (skipAuth). Rate limited 10/min per IP.
 * - Owner list/send: Bearer, paid only (403 if not paid).
 * See docs/API-REFERENCE.md.
 */

import api, { normalizeApiError } from './api'

/**
 * Subscribe to a restaurant's mailing list (public, no auth).
 * POST /api/public/restaurants/{slug}/mailing-list/subscribe. Body: { email, locale?, captcha_token? }.
 * @param {string} restaurantSlug - Restaurant slug from public page context
 * @param {string} email - Subscriber email
 * @param {string} [locale] - Optional locale (e.g. from i18n) to store with the subscription
 * @param {string} [captchaToken] - Optional reCAPTCHA token when backend has reCAPTCHA enabled
 * @returns {Promise<{ message: string }>} Resolves with { message: 'Subscribed.' } or { message: "You're already subscribed." }
 * @throws Normalized error on 403 (mailing list not available), 422 (validation), 429 (rate limit)
 */
export async function subscribeMailingList(restaurantSlug, email, locale = null, captchaToken = null) {
  const body = { email: String(email).trim() }
  if (locale && String(locale).trim()) body.locale = String(locale).trim()
  if (captchaToken) body.captcha_token = captchaToken
  const { data } = await api.post(
    `/public/restaurants/${encodeURIComponent(restaurantSlug)}/mailing-list/subscribe`,
    body,
    { skipAuth: true }
  )
  return data
}

/**
 * List mailing list subscribers for a restaurant (owner, Bearer, paid only).
 * GET /api/restaurants/{restaurant}/mailing-list. Paginated (50 per page).
 * @param {string} restaurantUuid - Restaurant uuid
 * @param {{ page?: number }} [params] - Optional page (default 1)
 * @returns {Promise<{ data: Array<{ uuid: string, email: string, subscribed_at: string }>, meta: object }>}
 */
export async function getMailingList(restaurantUuid, params = {}) {
  const { data } = await api.get(`/restaurants/${restaurantUuid}/mailing-list`, { params })
  return data
}

/**
 * Send an email to selected subscribers (owner, Bearer, paid only).
 * POST /api/restaurants/{restaurant}/mailing-list/send. Body: { subject, body, subscriber_uuids } (HTML).
 * @param {string} restaurantUuid - Restaurant uuid
 * @param {{ subject: string, body: string, subscriber_uuids: string[] }} payload - Subject, HTML body, and UUIDs of subscribers to send to
 * @returns {Promise<{ message: string, sent_count: number }>}
 */
export async function sendMailingListEmail(restaurantUuid, payload) {
  const { data } = await api.post(`/restaurants/${restaurantUuid}/mailing-list/send`, {
    subject: payload.subject,
    body: payload.body,
    subscriber_uuids: payload.subscriber_uuids ?? [],
  })
  return data
}

/**
 * Remove selected subscribers from the mailing list (owner, Bearer, paid only).
 * POST /api/restaurants/{restaurant}/mailing-list/remove. Body: { subscriber_uuids }.
 * @param {string} restaurantUuid - Restaurant uuid
 * @param {{ subscriber_uuids: string[] }} payload - UUIDs of subscribers to remove
 * @returns {Promise<{ message: string, removed_count: number }>}
 */
export async function removeMailingListSubscribers(restaurantUuid, payload) {
  const { data } = await api.post(`/restaurants/${restaurantUuid}/mailing-list/remove`, {
    subscriber_uuids: payload.subscriber_uuids ?? [],
  })
  return data
}

export const mailingListService = {
  subscribeMailingList,
  getMailingList,
  sendMailingListEmail,
  removeMailingListSubscribers,
}

export { normalizeApiError }
